/* eslint-disable import/no-cycle */
import { createActions } from 'redux-actions';
import { goodEnoughUUID } from '../../../../../../utils/uuid';

const {
	addChartSettings,
	removeChartSettings,
	setChartShowDecimatedData,
	setChartUseSawtooth,
	setChartHasSawtoothData,
	setChartUseNormalizedDepol,
	setChartUseInterpolatedDepol,
	setShowSpatial,
	setChartSurveys,
	addChartSurvey,
	removeChartSurvey,
	setChartPrimarySurveyId,
	setChartDepolSurveyId,
	setSelectedRemediationGroup,
	setSeriesVisibilityDataStore,
	setSeriesVisibility,
	setShowConfirmationForCustomGroups,
	setConfirmationMessageKey,
	incrementSeriesVisibilityResetToken,
	setSeriesCache,

	setChartUseThreshold,
	setChartUseShadow,
	incrementInputThresholdResetToken,
	setChartPendingThresholds,
	setChartPendingLessNegativeThreshold,
	setChartPendingMoreNegativeThreshold,
	clearChartPendingThresholds,
	setChartThresholds,
	setChartLessNegativeThreshold,
	setChartMoreNegativeThreshold,
} = createActions(
	{
		// BASE
		ADD_CHART_SETTINGS: (chartId, settings) => {
			return { chartId, settings };
		},
		REMOVE_CHART_SETTINGS: chartId => {
			return { chartId };
		},
		// DECIMATED DATA
		SET_CHART_SHOW_DECIMATED_DATA: (chartId, showDecimatedData) => {
			return { chartId, showDecimatedData };
		},
		// SAWTOOTH
		SET_CHART_USE_SAWTOOTH: (chartId, useSawtooth) => {
			return { chartId, useSawtooth };
		},
		SET_CHART_HAS_SAWTOOTH_DATA: (chartId, hasSawtoothData) => {
			return { chartId, hasSawtoothData };
		},
		// USE NORMALIZED
		SET_CHART_USE_NORMALIZED_DEPOL: (chartId, useNormalizedDepol) => {
			return { chartId, useNormalizedDepol };
		},
		// USE INTERPOLATED
		SET_CHART_USE_INTERPOLATED_DEPOL: (chartId, useInterpolatedDepol) => {
			return { chartId, useInterpolatedDepol };
		},
		// SHOW SPATIAL
		SET_SHOW_SPATIAL: (chartId, showSpatial) => {
			return { chartId, showSpatial };
		},
		// SURVEYS
		SET_CHART_SURVEYS: (chartId, surveys) => {
			return { chartId, surveys };
		},
		ADD_CHART_SURVEY: (chartId, survey) => {
			return { chartId, survey };
		},
		REMOVE_CHART_SURVEY: (chartId, survey) => {
			return { chartId, survey };
		},
		SET_CHART_PRIMARY_SURVEY_ID: (chartId, surveyId) => {
			return { chartId, surveyId };
		},
		SET_CHART_DEPOL_SURVEY_ID: (chartId, surveyId) => {
			return { chartId, surveyId };
		},
		SET_SELECTED_REMEDIATION_GROUP: (chartId, group, groupIndex) => {
			return { chartId, group, groupIndex };
		},
		SET_SERIES_VISIBILITY_DATA_STORE: (chartId, dataStore) => {
			return { chartId, dataStore };
		},

		// @note - its possible for the same data set to be plotted via multiple series
		// @example - on/off surveys
		SET_SERIES_VISIBILITY: (
			chartId,
			{
				dataSetKey, // could be a survey id - could be the name of a series
				propKey, // name of property which is plotted on chart
				visibilityFlagKey, // an override prop for propKey // allows us to track vis for BC without tying it specificially to an on or off prop.
				isVisible
			},
			{ overwriteChildStore } = {}
		) => {
			return {
				chartId,
				dataSetKey,
				visibilityFlagKey,
				propKey,
				isVisible,
				overwriteChildStore
			};
		},

		SET_SHOW_CONFIRMATION_FOR_CUSTOM_GROUPS: (chartId, showConfirmation) => {
			return { chartId, showConfirmation };
		},

		SET_CONFIRMATION_MESSAGE_KEY: (chartId, confirmationMessageKey) => {
			return { confirmationMessageKey, chartId };
		},
		INCREMENT_SERIES_VISIBILITY_RESET_TOKEN: chartId => {
			const token = goodEnoughUUID();
			return { chartId, token };
		},
		SET_SERIES_CACHE: (chartId, series) => {
			return { chartId, series };
		},


		/**
		 * THRESHOLDS
		 */

		SET_CHART_USE_THRESHOLD: (chartId, useThreshold) => {
			return { chartId, useThreshold };
		},
		SET_CHART_USE_SHADOW: (chartId, useShadow) => {
			return { chartId, useShadow };
		},
		INCREMENT_INPUT_THRESHOLD_RESET_TOKEN: chartId => {
			const token = goodEnoughUUID();
			return { chartId, token };
		},
		SET_CHART_PENDING_THRESHOLDS: (chartId, lessNegativeThreshold, moreNegativeThreshold) => {
			return { chartId, lessNegativeThreshold, moreNegativeThreshold };
		},
		SET_CHART_PENDING_LESS_NEGATIVE_THRESHOLD: (chartId, lessNegativeThreshold) => {
			return { chartId, lessNegativeThreshold };
		},
		SET_CHART_PENDING_MORE_NEGATIVE_THRESHOLDS: (chartId, moreNegativeThreshold) => {
			return { chartId, moreNegativeThreshold };
		},
		CLEAR_CHART_PENDING_THRESHOLDS: chartId => {
			return { chartId };
		},
		SET_CHART_THRESHOLDS: (chartId, lessNegativeThreshold, moreNegativeThreshold) => {
			return { chartId, lessNegativeThreshold, moreNegativeThreshold };
		},
		SET_CHART_LESS_NEGATIVE_THRESHOLD: (chartId, lessNegativeThreshold) => {
			return { chartId, lessNegativeThreshold };
		},
		SET_CHART_MORE_NEGATIVE_THRESHOLD: (chartId, moreNegativeThreshold) => {
			return { chartId, moreNegativeThreshold };
		},
	},
	{
		prefix: 'ChartsSettings'
	}
);

// eslint-disable-next-line import/prefer-default-export
export {
	addChartSettings,
	removeChartSettings,
	setChartShowDecimatedData,
	setChartUseSawtooth,
	setChartHasSawtoothData,
	setChartUseNormalizedDepol,
	setShowConfirmationForCustomGroups,
	setConfirmationMessageKey,
	incrementSeriesVisibilityResetToken,
	setSeriesCache,
	setChartUseInterpolatedDepol,
	setShowSpatial,
	setChartSurveys,
	addChartSurvey,
	removeChartSurvey,
	setChartPrimarySurveyId,
	setChartDepolSurveyId,
	setSelectedRemediationGroup,
	setSeriesVisibilityDataStore,
	setSeriesVisibility,

	setChartUseThreshold,
	setChartUseShadow,
	incrementInputThresholdResetToken,
	setChartPendingThresholds,
	setChartPendingLessNegativeThreshold,
	setChartPendingMoreNegativeThreshold,
	clearChartPendingThresholds,
	setChartThresholds,
	setChartLessNegativeThreshold,
	setChartMoreNegativeThreshold,
};
